import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getMetrics } from '../../data/mockApi';

const { FiTrendingUp, FiTrendingDown } = FiIcons;

// Dark theme color mappings
const DARK_METRIC_COLORS = {
  emerald: {
    bg: 'bg-[#111916]',
    border: 'border-[#243129]',
    icon: 'bg-gradient-to-br from-lime-400 to-lime-600',
    glow: 'hover:shadow-lime-500/10'
  },
  blue: {
    bg: 'bg-[#111916]',
    border: 'border-[#243129]',
    icon: 'bg-gradient-to-br from-sky-400 to-sky-600',
    glow: 'hover:shadow-sky-500/10'
  },
  amber: {
    bg: 'bg-[#111916]',
    border: 'border-[#243129]',
    icon: 'bg-gradient-to-br from-amber-400 to-amber-600',
    glow: 'hover:shadow-amber-500/10'
  },
  purple: {
    bg: 'bg-[#111916]',
    border: 'border-[#243129]',
    icon: 'bg-gradient-to-br from-violet-400 to-violet-600',
    glow: 'hover:shadow-violet-500/10'
  }
};

const MetricsGrid = () => {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const data = await getMetrics();
      setMetrics(data);
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="bg-[#111916] rounded-xl p-5 border border-[#243129]">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="h-4 bg-[#1a2420] rounded w-24 mb-3 skeleton"></div>
                <div className="h-8 bg-[#1a2420] rounded w-32 mb-3 skeleton"></div>
                <div className="h-3 bg-[#1a2420] rounded w-20 skeleton"></div>
              </div>
              <div className="w-12 h-12 bg-[#1a2420] rounded-xl skeleton"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {metrics.map((metric, index) => {
        const colorClasses = DARK_METRIC_COLORS[metric.color] || DARK_METRIC_COLORS.emerald;
        const isUp = metric.trend === 'up';

        return (
          <motion.div
            key={metric.title}
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.4, delay: index * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
            whileHover={{ y: -2, transition: { duration: 0.2 } }}
            className={`${colorClasses.bg} rounded-xl p-5 border ${colorClasses.border} ${colorClasses.glow} hover:border-[#84cc16]/30 transition-all duration-300 cursor-pointer group relative overflow-hidden`}
          >
            {/* Subtle glow on hover */}
            <div className="absolute inset-0 bg-gradient-to-br from-lime-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            <div className="flex items-center justify-between relative z-10">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-mono text-[#6b8772] uppercase tracking-wider mb-1">{metric.title}</p>
                <p className="text-2xl font-display font-semibold text-[#e8f5e9] mt-1 tabular-nums">{metric.value}</p>
                <div className="flex items-center mt-2 gap-1.5">
                  <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${isUp ? 'bg-lime-500/10' : 'bg-red-500/10'}`}>
                    <SafeIcon
                      icon={isUp ? FiTrendingUp : FiTrendingDown}
                      className={`w-3 h-3 ${isUp ? 'text-lime-400' : 'text-red-400'}`}
                    />
                    <span className={`text-xs font-mono font-medium ${isUp ? 'text-lime-400' : 'text-red-400'}`}>
                      {metric.change}
                    </span>
                  </div>
                  <span className="text-[10px] font-mono text-[#4a5f50]">vs last period</span>
                </div>
              </div>
              <div className={`${colorClasses.icon} rounded-xl p-3 shadow-lg`}>
                <SafeIcon icon={metric.icon} className="w-5 h-5 text-[#050807]" />
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default MetricsGrid;
